import { createRootRoute, Link, Outlet, useMatches } from "@tanstack/react-router";
import { useLayoutEffect } from "react";
import { appConfig } from "../../../app.config";
import { AiNotice, LegalLinks } from "./lab/legal";
import { pageWidthClass } from "./lib/layout";

declare module "@tanstack/react-router" {
  interface StaticDataRouteOption {
    /** Vrai : ni en-tete, ni pied de page, ni fond. Pour une source navigateur d'OBS. */
    pageNue?: boolean;
  }
}

/**
 * Le fond de `body` devient transparent le temps d'une page nue ; celui d'avant revient en la
 * quittant. Au premier affichage, `styles.css` a deja peint `body` avant que le JS tourne : cet
 * instant-la est couvert par le CSS personnalise par defaut de la source navigateur d'OBS.
 */
function useBodyTransparent(active: boolean): void {
  useLayoutEffect(() => {
    if (!active) return;
    const before = document.body.style.background;
    document.body.style.background = "transparent";
    return () => {
      document.body.style.background = before;
    };
  }, [active]);
}

/**
 * Mise en page commune : en-tete, contenu, pied de page. Fichier du template.
 * Une route qui declare `staticData: { pageNue: true }` rend son composant seul, sans ce decor.
 * Les trois blocs partagent la meme colonne, reglee par `layout.width` dans `app.config.ts`.
 */
function RootLayout() {
  const pageNue = useMatches({ select: (matches) => matches.at(-1)?.staticData.pageNue === true });
  useBodyTransparent(pageNue);
  if (pageNue) return <Outlet />;
  const width = pageWidthClass(appConfig);
  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="border-b border-border">
        <nav className={`mx-auto flex ${width} items-center justify-between px-4 py-4`}>
          <Link to="/" className="font-semibold">
            {appConfig.name}
          </Link>
          <a className="text-sm text-muted-foreground" href={`mailto:${appConfig.contactEmail}`}>
            Contact
          </a>
        </nav>
      </header>
      <main className={`mx-auto w-full ${width} flex-1 px-4 py-10`}>
        <Outlet />
      </main>
      <footer className="border-t border-border">
        <div
          className={`mx-auto flex ${width} flex-col gap-2 px-4 py-6 text-sm text-muted-foreground`}
        >
          <p>
            {appConfig.name} - {appConfig.domain}
          </p>
          {/* Liens legaux et mention IA : poses par la couche Lab, sur toutes les pages. */}
          <LegalLinks />
          <AiNotice />
        </div>
      </footer>
    </div>
  );
}

function NotFound() {
  return (
    <section>
      <h1 className="text-xl font-semibold">Page introuvable</h1>
      <p className="mt-2 text-muted-foreground">Cette adresse ne mene nulle part.</p>
    </section>
  );
}

export const rootRoute = createRootRoute({
  component: RootLayout,
  notFoundComponent: NotFound,
});
